import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AllergensPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ allergens: [], items: [], matrix: {} });
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('Todos');

  useEffect(() => {
    document.title = 'Alergenos · Taj Mahal San Fernando';
    axios.get(`${API}/allergens`)
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const set = new Set(data.items.map(i => i.category));
    return ['Todos', ...Array.from(set)];
  }, [data.items]);

  const filtered = useMemo(() => {
    if (activeCategory === 'Todos') return data.items;
    return data.items.filter(i => i.category === activeCategory);
  }, [data.items, activeCategory]);

  const has = (itemId, key) => (data.matrix[itemId] || []).includes(key);

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="allergens-page">
      <header className="sticky top-0 z-50 glass border-b border-[#2D231E]/10 print:hidden">
        <div className="container-max flex items-center h-16 gap-3">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-[#5C4D43] hover:text-[#C44536] transition-colors" data-testid="back-home">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-sans text-sm font-medium">Volver al Inicio</span>
          </button>
          <h1 className="font-serif text-xl font-bold text-[#2D231E] mx-auto">Alergenos</h1>
          <Button onClick={() => window.print()} variant="outline" size="sm" data-testid="print-allergens">
            <Printer className="w-4 h-4 mr-1.5" /> Imprimir
          </Button>
        </div>
      </header>

      <div className="container-max py-8">
        <div className="bg-[#C44536]/10 border-l-4 border-[#C44536] rounded-r-xl p-5 mb-6" role="alert">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 text-[#C44536] shrink-0 mt-0.5" />
            <div className="text-sm text-[#5C4D43] leading-relaxed">
              <p className="font-semibold text-[#2D231E] mb-1">
                Informacion de alergenos — Reglamento (UE) 1169/2011
              </p>
              <p>
                Los iconos en cada celda indican que el plato <strong>contiene</strong> el alergeno como ingrediente. Nuestra cocina manipula a diario los 14 alergenos de declaracion obligatoria, por lo que <strong>no podemos garantizar la ausencia total de trazas</strong> debido a la contaminacion cruzada (utensilios, superficies, aceite compartido). Si tiene una alergia grave o anafilactica, informenos antes de pedir al{' '}
                <strong>+34 602 704 745</strong> o por email a <strong>Tajmahalsafernando@gmail.com</strong>.
              </p>
            </div>
          </div>
        </div>

        {/* Category tabs */}
        <div className="flex flex-wrap gap-2 mb-4 print:hidden" data-testid="category-tabs">
          {categories.map(c => (
            <button
              key={c}
              onClick={() => setActiveCategory(c)}
              data-testid={`cat-${c}`}
              className={`px-4 py-1.5 rounded-full text-sm font-sans transition-colors ${
                activeCategory === c
                  ? 'bg-[#C44536] text-white'
                  : 'bg-white border border-[#E8DEC8] text-[#2D231E] hover:border-[#C44536]'
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-center py-12 text-[#5C4D43]">Cargando…</p>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-[#E8DEC8] bg-white shadow-sm">
            <table className="w-full text-sm" data-testid="allergens-table">
              <thead className="bg-[#F5EFE6]">
                <tr>
                  <th className="text-left py-3 px-3 font-serif text-[#2D231E] sticky left-0 bg-[#F5EFE6] z-10 min-w-[200px]">
                    Plato
                  </th>
                  {data.allergens.map(a => (
                    <th key={a.key} className="py-3 px-2 text-center font-sans text-xs font-semibold text-[#5C4D43] whitespace-nowrap">
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="text-xl" aria-hidden="true">{a.icon}</span>
                        <span className="text-[10px] font-normal">{a.label}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((item, idx) => (
                  <tr
                    key={item.id}
                    className={idx % 2 === 0 ? 'bg-white' : 'bg-[#FDFBF7]'}
                    data-testid={`row-${item.id}`}
                  >
                    <td className="py-2 px-3 font-sans text-[#2D231E] sticky left-0 bg-inherit z-10">
                      <div>{item.name}</div>
                      <div className="text-xs text-[#8A7A6D]">{item.category}</div>
                    </td>
                    {data.allergens.map(a => (
                      <td key={a.key} className="py-2 px-2 text-center">
                        {has(item.id, a.key) ? (
                          <span className="inline-flex w-7 h-7 items-center justify-center rounded-full bg-[#C44536]/10 text-base" aria-label={`Contiene ${a.label}`} title={a.label}>
                            <span aria-hidden="true">{a.icon}</span>
                          </span>
                        ) : (
                          <span className="text-[#E8DEC8]">·</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="mt-6 text-xs text-[#8A7A6D] font-sans">
          Ultima actualizacion gestionada por el restaurante. Consulta los{' '}
          <a href="/terminos" className="underline text-[#C44536]" target="_blank" rel="noopener noreferrer">
            Terminos y Condiciones · Seccion 8
          </a>{' '}
          para la informacion legal completa sobre alergenos y limitacion de responsabilidad.
        </p>
      </div>

      <style>{`
        @media print {
          body { background: white !important; }
          @page { size: A4 landscape; margin: 12mm; }
        }
      `}</style>
    </div>
  );
};

export default AllergensPage;
